import React, { useEffect, useMemo, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../utils/Hooks";
import { Link, useLocation, useParams } from "react-router-dom";
import { LoggedInView } from "../common/LoggedInView";
import Breadcrumbs from "../common/Breadcrumbs";
import ElogInput from "../common/form/ElogInput";
import ElogSelect, { Option } from "../common/form/ElogSelect";
import ElogCheckbox from "../common/form/ElogCheckbox";
import CategoryType from "../../models/CategoryType";
import { getCountries } from "../../store/reducers/CountriesSlice";
import { apiClient } from "../../utils/ApiClient";
import ElogTextarea from "../common/form/ElogTextarea";
import {
    updateVendor,
    resetUpdateVendorStatus,
    getCurrentVendor, createVendor,
} from "../../store/reducers/VendorsSlice";
import { RequestStatus } from "../../utils/Enums";
import {BreadcrumbLink} from "../../models/BreadcrumbLink";
import {useFormik, Field, Form, FormikProvider} from "formik";

interface ModifyVendorScreenProps {}

export interface ModifyVendorFormProps {
    name: string;
    insurance: boolean,
    wca: boolean,
    wcaId: string;
    tempInsurance: number;
    comment: string;
    banned: boolean;
    groups: string[];
}

export const ModifyVendorScreen: React.FC<ModifyVendorScreenProps> = () => {
    const dispatch = useAppDispatch();
    const location = useLocation();

    const { id } = useParams();

    const vendorsState = useAppSelector((state) => state.vendors);
    const countriesState = useAppSelector((state) => state.countries);

    const requestStatus: RequestStatus = vendorsState.updateVendorStatus;

    const breadcrumbLinks: BreadcrumbLink[] = [
        { text: "Hankijad", url: "/vendors" },
        { text: "Muuda hankijat", url: `/vendors/${id}/modify` },
    ];

    const [countries, setCountries] = useState<Option[]>([]);
    const [categoryTypes, setCategoryTypes] = useState<CategoryType[]>([]);

    useEffect(() => {
        dispatch(resetUpdateVendorStatus());
    }, [location]);

    useEffect(() => {
        dispatch(getCurrentVendor({ id }));
    }, [id]);

    useEffect(() => {
        const countryOptions: Option[] = [];

        countriesState.countries.map((country) => {
            const option: Option = {
                value: String(country.id),
                label: country.name,
            };

            countryOptions.push(option);
        });

        setCountries(countryOptions);
    }, [countriesState.countries]);

    useEffect(() => {
        dispatch(getCountries());

        apiClient
            .get("category-types/all-by-category")
            .then((response) => {
                setCategoryTypes(response.data);
            });
    }, []);

    const selectedCategoryIds = useMemo(() => {
        return vendorsState.currentVendor?.categories?.map((category) =>
            String(category.id)
        );
    }, [vendorsState.currentVendor]);

    // @ts-ignore
    const formik = useFormik({
        initialValues: {
            name: vendorsState?.currentVendor?.name ?? "",
            insurance: vendorsState?.currentVendor?.insurance ?? false,
            wca: vendorsState?.currentVendor?.wca ?? false,
            wcaId: vendorsState?.currentVendor?.wca_id ?? "",
            tempInsurance: vendorsState?.currentVendor?.temp_insurance ?? false,
            comment: vendorsState?.currentVendor?.comment ?? "",
            banned: vendorsState?.currentVendor?.banned_at ?? false,
            problem: vendorsState?.currentVendor?.problem ?? false,
            groups: selectedCategoryIds,
        },
        validate: (values) => {
            const errors: Partial<ModifyVendorFormProps> = {};

            if (values.wca && !values.wcaId.trim()) {
                errors.wcaId = 'WCA ID on kohustuslik, kui WCA on märgitud';
            }

            return errors;
        },
        onSubmit: (values: any) => {
            dispatch(updateVendor({ id, vendorData: values }));
        },
        enableReinitialize: true
    });

    const {values, setFieldValue, handleSubmit} = formik;

    return (
        <LoggedInView fullWidth={false}>
            <Breadcrumbs
                links={breadcrumbLinks}
            />
            {requestStatus === RequestStatus.Success && (
                <div className="message msg-ok">
                    <div>
                        <h2>Hankija andmed uuendatud</h2>
                    </div>
                </div>
            )}
            {requestStatus === RequestStatus.Failed &&
            vendorsState.errors.length > 0 && (
                <div className="message msg-error">
                    <div>
                        <h2>Viga:</h2>
                        {vendorsState.errors.map((error, index) => (
                            <span key={index}>
                                    {error}
                                <br />
                                </span>
                        ))}
                    </div>
                </div>
            )}
            <FormikProvider value={formik}>
                <Form onSubmit={handleSubmit}>
                    <div className="form">
                        <h2>
                            <span>Hankija andmed</span>
                        </h2>
                        <table className="form">
                            <tbody>
                            <ElogInput
                                label="Nimi"
                                name="name"
                                mandatory={true}
                            />
                            <ElogCheckbox
                                label="Kindlustus"
                                name="insurance"
                            />
                            <ElogCheckbox
                                label="WCA"
                                name="wca"
                            />
                            <ElogInput
                                label="WCA ID"
                                name="wcaId"
                                mandatory={values.wca}
                            />
                            <ElogCheckbox
                                label="Ajutine kindlustus"
                                name="tempInsurance"
                            />
                            <ElogCheckbox
                                label="Keelatud"
                                name="banned"
                            />
                            <ElogCheckbox
                                label="Probleem"
                                name="problem"
                            />
                            <ElogTextarea
                                label="Kommentaar"
                                name="comment"
                            />
                            </tbody>
                        </table>
                    </div>

                    {categoryTypes.map((categoryType) => (
                        <div key={categoryType.id} className="form">
                            <h2>
                                <span>{categoryType.name}</span>
                            </h2>
                            {categoryType?.categories?.map((category) => (
                                <Field
                                    key={category.id}
                                    name="groups"
                                    type="checkbox"
                                    value={String(category.id)}
                                    render={(props: { field: { value: any; onChange: (e: React.ChangeEvent<any>) => void; onBlur: () => void; name: string } }) => (
                                        <>
                                            <label htmlFor={`group_${category.id}`}>{category.name}</label>
                                            <input
                                                {...props.field}
                                                id={`group_${category.id}`}
                                                type="checkbox" // Set the type attribute to "checkbox"
                                                onChange={(event) => props.field.onChange(event)}
                                                onBlur={() => undefined}
                                            />
                                        </>
                                    )}
                                />
                            ))}
                        </div>
                    ))}

                    <div className="action clear">
                        <p>
                            <button type="submit">
                                    <span>
                                        <strong>Muuda</strong>
                                    </span>
                            </button>
                            <Link to="/vendors">Tühista</Link>
                        </p>
                    </div>
                </Form>
            </FormikProvider>
        </LoggedInView>
    );
};
